import {
  Divider,
  Drawer,
  Grid,
  Typography,
  Paper,
} from '@material-ui/core';
import { useValidarCampos } from './useValidarCampos';
import { BancoResponse, FuncaoParticipante, GrupoAcesso, InstrumentoFinanceiro, ParticipanteResponse, RegistroStatus, TipoRegistroInstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest } from 'common/genericTypes';
import { Button, LgSpace, MdSpace, SmSpace } from 'm2m.components';
import Caracteristicas from 'pages/cotasDeFundo/registro/caracteristicas';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetCotasDeFundoDetails } from 'store/modules/cotasDeFundo/caracteristicas';
import { RootState } from 'store';
import { highlightErrors } from 'store/modules/cotasDeFundo';
import { addAlert } from 'store/modules/snackBar';
import { Finished } from './formularioFinished';
import { GETParticipantesResume } from 'requests/participante';

interface Props {
  title: string;
  handleSubmit(): Promise<{ codigo: string; ticket: string; hasError?: boolean } | undefined>;
  handleReturn(): void;
  handleSalvarRascunho(): Promise<string>;
  isEdit: boolean;
  codigo?: string;
  cotasDeFundoId?: string;
  submissionError?: boolean;
}

const FormularioCotasDeFundo: React.FC<Props> = (props) => {
  const {
    title,
    cotasDeFundoId,
    handleSubmit,
    handleReturn,
    handleSalvarRascunho,
    isEdit,
    codigo,
    submissionError,
  } = props;

  const isUsuarioOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
    const isUsuarioCadastroOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes; 
    const isParticipanteCustodiante =
      useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Custodiante;
  const isOperacoesEdit = isUsuarioOperacoes && isEdit;
  const isCustodianteEdit = isParticipanteCustodiante && isEdit;
  const isCadastroOperacoesEdit = isUsuarioCadastroOperacoes && isEdit;

  const typeOfRegister = useSelector((state: RootState) => state.cotasDeFundoCadastro.typeOfRegister);

  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantes);
  const getCotasDeFundoApi = useSelector((state: RootState) => state.cotasDeFundoApi.getCotasDeFundoDetalhes);
  const caracteristicas = useSelector((state: RootState) => state.cotasDeFundoCadastro.caracteristicas);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigoCotasDeFundo, setCodigoCotasDeFundo] = useState<string>('');
  const [ticket, setTicket] = useState<string>('');
  const [finished, setFinished] = useState<boolean>(false);
  const [statusCadastro, setStatusCadastro] = useState<string>();
  const [delimitacaoEfetuada, setDelimitacaoEfetuada] = useState<boolean>(false);

  const [participantes, setParticipantes] = useState<Array<ParticipanteResponse>>([]);
  const [openConfirmDrawer, setOpenConfirmDrawer] = useState(false);
  const [openConfirmDrawerAditamento, setOpenConfirmDrawerAditamento] = useState(false);
  const [openConfirmDrawerEdicao, setOpenConfirmDrawerEdicao] = useState(false);
  const [caracteristicasRef, setCaracteristicasRef] = useState<{ clearLocalFields: () => void } | null>(null);

  const dispatch = useDispatch();
  const camposInvalidos = useValidarCampos();
  
  const handleSubmitInternalBehaviour = async () => {
    try {
      const numeroEmissaoReservado = await reservarNumeroEmissao();
      if (!numeroEmissaoReservado) {
        setOpenConfirmDrawer(false);
        setOpenConfirmDrawerAditamento(false);
        setOpenConfirmDrawerEdicao(false);
        return;
      }
      
      const result = await handleSubmit();
      
      if (result && !submissionError && !(result as any).hasError) {
        setCodigoCotasDeFundo(result?.codigo ?? '');
        setTicket(result?.ticket ?? '');
        setFinished(true);
        setOpenConfirmDrawer(false);
        setOpenConfirmDrawerAditamento(false);
        setOpenConfirmDrawerEdicao(false);
      } else {
        setFinished(false);
        setOpenConfirmDrawer(false);
        setOpenConfirmDrawerAditamento(false);
        setOpenConfirmDrawerEdicao(false);
      }
    } catch (error) {
      setFinished(false);
      setOpenConfirmDrawer(false);
      setOpenConfirmDrawerAditamento(false);
      setOpenConfirmDrawerEdicao(false);
    }
  };

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas?.cnpjFundo) {
      dispatch(addAlert({ message: 'CNPJ do fundo não encontrado', severity: 'error' }));
      return false;
    }

    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjFundo,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.CotasDeFundo,
        externalId: participanteId,
      };
      
      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  const handleConcluirCadastro = async () => {
    const hasErrors = camposInvalidos.length > 0;
    
    if (hasErrors) {
      dispatch(highlightErrors(true));
      dispatch(
        addAlert({
          message: `Os seguintes campos não foram preenchidos corretamente: ${camposInvalidos.join(', ')}`,
          severity: 'error',
        })
      );
      return;
    }
    
    setOpenConfirmDrawer(true);
  };

  const handleClearAllFields = () => {
    dispatch(resetCotasDeFundoDetails());
    if (caracteristicasRef?.clearLocalFields) {
      caracteristicasRef.clearLocalFields();
    }
  };
  
  const validateFields = () => {
    return camposInvalidos.length > 0;
  };

  const finishButtonSwitch = () => {
    switch (typeOfRegister) {
      default:
        return (
          <>
            <Button variant="contained" color="primary" onClick={handleConcluirCadastro}>
              {'Concluir'}
            </Button>
          </>
        );
    }
  };

  const getParticipantes = useCallback(async () => {
    try {
      const result = await GETParticipantesResume(
        dispatch,
        {
          funcoes: [
            FuncaoParticipante.Escriturador,
            FuncaoParticipante.Custodiante,
            FuncaoParticipante.AdministradorFiduciario,
          ],
          status: RegistroStatus.Aprovado,
          suspenso: false
        },
        true);
      setParticipantes(result);
    } catch (error) { }
  }, [dispatch]);

  const getCotasDeFundoById = useCallback(async () => {
    if (cotasDeFundoId) {
      const result = await getCotasDeFundoApi(dispatch, cotasDeFundoId);
      setStatusCadastro(result.status);
      
      if (result.coordenadores && result.coordenadores.length > 0) {
        const hasNonNullValue = result.coordenadores.some(c => c.valor !== null);
        setDelimitacaoEfetuada(hasNonNullValue);
      } else {
        setDelimitacaoEfetuada(false);
      }
    }
  }, [dispatch, getCotasDeFundoApi])



  useEffect(() => {
    getParticipantes();
  }, [getParticipantes]);

  useEffect(() => {
    if (!statusCadastro) {
      getCotasDeFundoById();
    }
  }, [getCotasDeFundoById, statusCadastro])

  useEffect(() => {
    if (codigo) setCodigoCotasDeFundo(codigo);
  }, [codigo]);

  return (
    <>
      <Typography variant="h1">{title}</Typography>
      <MdSpace />
      <div>
        {finished ? (
          <Finished
            setFinished={setFinished}
            handleReturn={handleReturn}
            codigoCotasDeFundo={codigoCotasDeFundo}
            ticket={ticket}
          />
        ) : (
          <>
            <Caracteristicas 
              participantes={participantes} 
              codigo={codigoCotasDeFundo} 
              delimitacaoEfetuada={delimitacaoEfetuada}
              onRef={setCaracteristicasRef} 
            />
            <Paper
              style={{
                position: 'fixed',
                bottom: 0,
                width: '100%',
                marginLeft: '-40px',
                backgroundColor: '#fff',
                zIndex: 1,
              }}
            >
              <MdSpace />
              <Grid container justify="center" spacing={8} alignItems="center" style={{ width: '100%' }}>
                <Grid item>
                  <Button variant="outlined" color="secondary" onClick={handleClearAllFields} style={{ marginRight: '16px' }}>
                    {'Limpar'}
                  </Button>
                  {finishButtonSwitch()}
                </Grid>
              </Grid>
              <MdSpace />
            </Paper>
            <LgSpace />
          </>
        )}
      </div>
      <Drawer
        anchor="right"
        open={openConfirmDrawer}
        onClose={() => setOpenConfirmDrawer(false)}
        PaperProps={{ style: { padding: '40px', maxWidth: 460 } }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h3">{'Confirmação Emissão'}</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <MdSpace />
            <Typography variant="body2">
              {
                'Confirma que este Instrumento Financeiro não está cadastrado em outra depositária de valores mobiliários?'
              }
            </Typography>
            <MdSpace />
            <Button onClick={handleSubmitInternalBehaviour} variant="contained" color="primary" fullWidth>
              {'Sim, prosseguir'}
            </Button>
            <SmSpace />
            <Button onClick={() => setOpenConfirmDrawer(false)} variant="contained" color="error" fullWidth>
              {'Não, cancelar'}
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </>
  );
};

export default FormularioCotasDeFundo;
